import { effect, inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { FacilityContextService } from '../services/facility-context.service';
import { SecurityService } from '../services/security.service';
import { logger, type EpisodeContext, type PatientContext } from './logger';

/**
 * Keeps the logged-in user and the selected facility on every log event.
 * Runs once from an app initializer (needs an injection context for `effect`).
 */
export function initTelemetryContext(): void {
  const auth = inject(AuthService);
  const security = inject(SecurityService);
  const facilityContext = inject(FacilityContextService);

  effect(() => {
    const me = auth.me();
    if (!me || !auth.isAuthenticated()) {
      logger.setUser(null);
      logger.setPatient(null);
      logger.setEpisode(null);
      return;
    }
    const fullName = [me.firstName, me.lastName].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
    logger.setUser({
      id: me.id,
      email: me.email || null,
      username: fullName || null,
      userType: me.userType ?? null,
      roles: me.roles ?? [],
      orgId: security.getOrganizationId(),
      orgName: security.getClaimValue('OrganizationName') ?? (me.organization || null)
    });
  });

  effect(() => {
    const facility = facilityContext.selectedFacility();
    logger.setFacility(
      facility
        ? {
            facilityId: facility.id,
            facilityName: facility.name,
            orgId: security.getOrganizationId(),
            orgName: facilityContext.organizationName()
          }
        : null
    );
  });
}

/** Readable patient context from any patient-shaped web model. */
export function patientTelemetry(
  patient:
    | {
        id?: string | null;
        patientId?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        patientName?: string | null;
        fullName?: string | null;
        mrn?: string | null;
        dateOfBirth?: string | null;
        patientDateOfBirth?: string | null;
      }
    | null
    | undefined
): PatientContext | null {
  if (!patient) {
    return null;
  }
  const joined = [patient.firstName, patient.lastName].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
  return {
    patientId: patient.patientId ?? patient.id ?? null,
    name: joined || patient.fullName || patient.patientName || null,
    mrn: patient.mrn?.trim() || null,
    dob: (patient.dateOfBirth ?? patient.patientDateOfBirth)?.slice(0, 10) || null
  };
}

/** Readable episode context from any episode-shaped web model. */
export function episodeTelemetry(
  episode:
    | {
        id?: string | null;
        serviceName?: string | null;
        status?: string | null;
        clinicalDocumentationStatus?: string | null;
      }
    | null
    | undefined
): EpisodeContext | null {
  if (!episode) {
    return null;
  }
  return {
    episodeId: episode.id ?? null,
    serviceName: episode.serviceName ?? null,
    status: episode.status ?? null,
    stage: episode.clinicalDocumentationStatus ?? null
  };
}
